import { contains } from '@zambezi/sdk/common'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import TagLookupButton from './TagLookupButton'
import TagLookupRemove from './TagLookupRemove'

/**
 * @typedef TagLookupListProps
 * @property {boolean} disabled
 * @property {(tag:string) => (null | (tag:string, event: React.SyntheticEvent<Element, Event>) => void)} getOnTagRemove
 * @property {string} inputId
 * @property {import('../icons/interfaces').IconProps} removeIconProps
 * @property {string[]} tags
 * @property {string} tagsId
 * @property {number} tagsShown
 */

/**
 * @type {React.FunctionComponent<TagLookupListProps>}
 * @param {TagLookupListProps} param0
 */
export default function TagLookupList ({
  disabled,
  getOnTagRemove,
  inputId,
  removeIconProps,
  tags,
  tagsId,
  tagsShown
}) {
  const [activeTag, setActiveTag] = useState(0)
  const [focused, setFocused] = useState(false)
  /** @type {import('react').RefObject<Element>} */
  const elementRef = useRef()
  /** @type {import('react').RefObject<Element>} */
  const removeRef = useRef()
  const handleKeyDown = useCallback(
    (/** @type {React.KeyboardEvent} */ event) => {
      switch (event.key) {
        case ' ':
        // falls through
        case 'Enter': {
          const removeTag = getOnTagRemove(tags[activeTag])
          if (removeTag) {
            if (activeTag + 1 < tags.length) {
              setActiveTag(getTagIndex(tags, activeTag))
            } else {
              setActiveTag(getTagIndex(tags, activeTag - 1))
            }
            removeTag(tags[activeTag], event)
          }
          break
        }
        case 'ArrowRight':
        // falls through
        case 'ArrowDown':
          setActiveTag(getTagIndex(tags, activeTag + 1))
          break

        case 'ArrowLeft':
        // falls through
        case 'ArrowUp':
          setActiveTag(getTagIndex(tags, activeTag - 1))
          break

        case 'Home':
          setActiveTag(getTagIndex(tags, 0))
          break

        case 'End':
          setActiveTag(getTagIndex(tags, tags.length))
          break

        default:
          return
      }
      event.preventDefault()
    },
    [activeTag, tags]
  )
  const handleFocus = useCallback(
    (/** @type {React.FocusEvent} */ event) => {
      setFocused(true)
      if (contains(removeRef.current, event.target)) {
        setActiveTag(getTagIndex(tags, tagsShown))
      } else if (activeTag === -1) {
        setActiveTag(getTagIndex(tags, 0))
      }
      event.stopPropagation()
    },
    [activeTag, tags, tagsShown]
  )
  const handleBlur = useCallback((/** @type {React.FocusEvent} */ event) => {
    setFocused(false)
    event.stopPropagation()
  }, [])
  const handleRemoveHide = useCallback(() => {
    setActiveTag(getTagIndex(tags, tagsShown - 1))
    elementRef.current.focus()
  }, [tags, tagsShown])
  const getRemoveHandler = useCallback(
    (/** @type {string} */ tag) => {
      const remover = getOnTagRemove(tag)
      if (remover) {
        return (
          /** @type {string} */ tag,
          /** @type {React.SyntheticEvent} */ event
        ) => {
          remover(tag, event)
          setActiveTag(getTagIndex(tags, tagsShown - 1))
          elementRef.current.focus()
        }
      }
      return remover
    },
    [getOnTagRemove, activeTag, tags, tagsShown]
  )

  const children = []
  for (let i = 0; i < tags.length; i++) {
    if (i >= tagsShown) {
      children.push(
        <TagLookupRemove
          tags={tags.slice(i)}
          tagsId={tagsId}
          offset={tagsShown}
          key='TagLookupRemove\f'
          getTagRemoveHandler={getRemoveHandler}
          disabled={disabled}
          activeTag={focused ? activeTag : -1}
          onHide={handleRemoveHide}
          ref={removeRef}
        />
      )
      break
    }
    const tag = tags[i]
    children.push(
      <TagLookupButton
        tag={tag}
        key={tag}
        onTagRemove={getOnTagRemove(tag)}
        focused={focused && activeTag === i}
        disabled={disabled}
        removeIconProps={removeIconProps}
        id={`${tagsId}-${i}`}
        inputId={inputId}
      />
    )
  }

  useEffect(() => {
    if (activeTag < 0 || activeTag >= tags.length) {
      setActiveTag(0)
    }
  }, [tags, activeTag])
  const activeElement = focused && activeTag >= 0
  return children.length
    ? (
      <span
        className='zb-tag-lookup-tags'
        role='listbox'
        aria-orientation='horizontal'
        aria-label='Selected tags'
        aria-activedescendant={activeElement ? `${tagsId}-${activeTag}` : null}
        id={tagsId}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        ref={elementRef}
      >
        {children}
      </span>
      )
    : null
}

/**
 * Ensure to get a valid index from within `tags` for the `index`
 * or `-1` if `tags` is empty
 * @param {string[]} tags
 * @param {number} index
 */
function getTagIndex (tags, index) {
  const tagsLength = tags.length
  const tagIndex = index < tagsLength ? index : tagsLength - 1
  if (tagsLength) {
    return index < 0 ? 0 : tagIndex
  }
  return -1
}
